// Static scan of a widget's flattened document — the exact HTML that runs in the sandbox.
// It does not decide anything; it points reviewers (and authors) at the parts of the code
// worth reading first. The sandbox + CSP remain the real controls.

import type { WidgetManifest } from '../../services/myToolsApi';

export type ScanSeverity = 'high' | 'warn' | 'info';

export interface ScanFinding {
  code: string;
  severity: ScanSeverity;
  title: string;
  detail: string;
  count: number;
  /** Up to a few short excerpts around the matches. */
  samples: string[];
}

export interface ScanSummary { high: number; warn: number; info: number; total: number }

const SDK_MARKER = 'window.Momentum={';
const MAX_SAMPLES = 3;
const SAMPLE_RADIUS = 60;

/** Removes the host-injected CSP meta and SDK script so the scan only covers author code. */
export function stripInjectedRuntime(html: string): string {
  let out = html.replace(/<meta\s+http-equiv=["']Content-Security-Policy["'][^>]*>\s*/i, '');
  const scripts = /<script\b[^>]*>([\s\S]*?)<\/script>/gi;
  let match: RegExpExecArray | null;
  while ((match = scripts.exec(out)) !== null) {
    if (match[1].includes(SDK_MARKER)) {
      out = out.slice(0, match.index) + out.slice(match.index + match[0].length);
      break;
    }
  }
  return out;
}

/** Author-written script bodies (inline <script> blocks) — where dynamic-code checks matter. */
function scriptBodies(html: string): string {
  const parts: string[] = [];
  const scripts = /<script\b[^>]*>([\s\S]*?)<\/script>/gi;
  let match: RegExpExecArray | null;
  while ((match = scripts.exec(html)) !== null) parts.push(match[1]);
  // Inline event handlers count as script too.
  const handlers = /\son[a-z]+\s*=\s*(?:"([^"]*)"|'([^']*)')/gi;
  while ((match = handlers.exec(html)) !== null) parts.push(match[1] ?? match[2] ?? '');
  return parts.join('\n');
}

/** The document with script bodies removed, for checks that are about markup only. */
function markupOnly(html: string): string {
  return html.replace(/<script\b[^>]*>[\s\S]*?<\/script>/gi, '<script></script>');
}

function mergeHits(...hits: { count: number; samples: string[] }[]): { count: number; samples: string[] } {
  return {
    count: hits.reduce((n, h) => n + h.count, 0),
    samples: hits.flatMap(h => h.samples).slice(0, MAX_SAMPLES),
  };
}

function collect(source: string, pattern: RegExp): { count: number; samples: string[] } {
  const samples: string[] = [];
  let count = 0;
  const re = new RegExp(pattern.source, pattern.flags.includes('g') ? pattern.flags : pattern.flags + 'g');
  let match: RegExpExecArray | null;
  while ((match = re.exec(source)) !== null) {
    count++;
    if (samples.length < MAX_SAMPLES) {
      const start = Math.max(0, match.index - SAMPLE_RADIUS);
      const end = Math.min(source.length, match.index + match[0].length + SAMPLE_RADIUS);
      samples.push(source.slice(start, end).replace(/\s+/g, ' ').trim());
    }
    if (match[0].length === 0) re.lastIndex++;
  }
  return { count, samples };
}

function normalizeOrigin(url: string): string | null {
  try { return new URL(url).origin.toLowerCase(); } catch { return null; }
}

export function scanWidgetHtml(rawHtml: string, manifest?: WidgetManifest | null): ScanFinding[] {
  const html = stripInjectedRuntime(rawHtml || '');
  const js = scriptBodies(html);
  const findings: ScanFinding[] = [];
  const push = (code: string, severity: ScanSeverity, title: string, detail: string, hit: { count: number; samples: string[] }) => {
    if (hit.count > 0) findings.push({ code, severity, title, detail, count: hit.count, samples: hit.samples });
  };

  // ── Dynamic code execution ────────────────────────────────────────────────
  push('dynamic-code', 'high', 'Runs code built at runtime',
    'eval / new Function execute strings as code, which hides what the widget really does from a reviewer.',
    collect(js, /\beval\s*\(|\bnew\s+Function\s*\(|(?<![.\w])Function\s*\(/));
  push('string-timer', 'warn', 'Timer with a string argument',
    'setTimeout/setInterval with a string is eval in disguise.',
    collect(js, /\bset(?:Timeout|Interval)\s*\(\s*["'`]/));
  push('document-write', 'warn', 'document.write',
    'Rewrites the document at runtime; usually a sign of injected markup.',
    collect(js, /\bdocument\.write(?:ln)?\s*\(/));
  push('script-injection', 'warn', 'Creates script elements at runtime',
    'Dynamically inserted <script> tags are blocked by the CSP, but their presence suggests the code expected to load more code.',
    collect(js, /createElement\s*\(\s*["']script["']\s*\)/i));
  // Script bodies are already in `js`; scan only the markup around them for the meta form, or
  // every script hit is counted twice. A `var location = …` declaration isn't navigation.
  push('self-navigation', 'warn', 'Navigates its own frame',
    'location.href / assign / replace or <meta http-equiv="refresh"> would replace the widget with another page. Momentum AMP stops this and blanks the widget, so it is at best broken and at worst an attempt to show a look-alike page.',
    mergeHits(
      collect(js, /(?<!\b(?:var|let|const)\s+)\blocation\s*(?:\.href\s*=(?!=)|\.assign\s*\(|\.replace\s*\(|=(?!=))/),
      collect(markupOnly(html), /<meta\b[^>]*http-equiv\s*=\s*["']?refresh/i)));

  // ── Obfuscation ───────────────────────────────────────────────────────────
  push('encoded-blob', 'warn', 'Large encoded blob',
    'A long base64 string in code (not a data: image/font URI). Decode it to see what it holds.',
    collect(js.replace(/data:[a-z/+.-]+;base64,[A-Za-z0-9+/=]+/gi, ''), /[A-Za-z0-9+/]{1200,}={0,2}/));
  push('atob', 'info', 'Decodes base64 at runtime',
    'atob() on its own is harmless; combined with dynamic code or network calls it is how payloads get hidden.',
    collect(js, /\batob\s*\(/));
  {
    const escapes = collect(js, /\\x[0-9a-fA-F]{2}|\\u[0-9a-fA-F]{4}/);
    if (escapes.count >= 40) {
      findings.push({ code: 'escaped-strings', severity: 'warn', title: 'Heavily escaped strings', detail: `${escapes.count} hex/unicode escapes — typical of obfuscated code.`, count: escapes.count, samples: escapes.samples });
    }
  }
  push('fromcharcode', 'warn', 'Builds strings from character codes',
    'String.fromCharCode chains are a common way to hide keywords like "eval" or URLs.',
    collect(js, /String\.fromCharCode\s*\([^)]{40,}\)/));
  {
    const longest = js.split('\n').reduce((m, line) => Math.max(m, line.length), 0);
    if (longest > 5000) {
      findings.push({ code: 'minified', severity: 'info', title: 'Minified code', detail: `Longest line is ${longest.toLocaleString()} characters. Ask the author for readable source if you need to review logic.`, count: 1, samples: [] });
    }
  }

  // ── Host / bridge misuse ──────────────────────────────────────────────────
  push('raw-postmessage', 'info', 'Talks to the host directly',
    'Uses window.parent / postMessage instead of the Momentum SDK. The host only honours the documented messages, so this is not dangerous, but it is unusual.',
    collect(js, /\bwindow\.(?:parent|top)\b|\bparent\.postMessage\s*\(|\btop\.postMessage\s*\(/));
  push('storage-access', 'info', 'Uses browser storage or cookies',
    'localStorage / document.cookie throw inside the sandbox; the widget should use Momentum.storage.',
    collect(js, /\b(?:localStorage|sessionStorage|indexedDB)\b|document\.cookie/));

  // ── Network ───────────────────────────────────────────────────────────────
  push('network-api', 'info', 'Uses network APIs',
    'fetch / XMLHttpRequest / WebSocket / sendBeacon can only reach https:// addresses written out in the code — those are detected and requested at upload; anything else is blocked. Momentum.fetch (Momentum AMP data) is not affected.',
    // Momentum.fetch is the host bridge (a postMessage), not a network request.
    collect(js, /(?<!Momentum\.)\bfetch\s*\(|\bXMLHttpRequest\b|\bWebSocket\s*\(|navigator\.sendBeacon\s*\(|\bEventSource\s*\(/));
  {
    const urls = collect(html, /https?:\/\/[^\s"'<>)`]+/i);
    const declared = new Set((manifest?.permissions ?? [])
      .filter(p => p.toLowerCase().startsWith('network:'))
      .map(p => normalizeOrigin(p.slice('network:'.length)))
      .filter((o): o is string => !!o));
    const origins = new Map<string, number>();
    const re = /https?:\/\/[^\s"'<>)`]+/gi;
    let m: RegExpExecArray | null;
    while ((m = re.exec(html)) !== null) {
      const origin = normalizeOrigin(m[0]);
      if (origin) origins.set(origin, (origins.get(origin) ?? 0) + 1);
    }
    const undeclared = Array.from(origins.keys()).filter(o => !declared.has(o) && !/^https:\/\/(www\.)?(w3\.org|schema\.org)$/.test(o));
    if (undeclared.length) {
      findings.push({
        code: 'undeclared-origins', severity: 'info', title: 'References external sites',
        detail: `Mentions ${undeclared.length} origin${undeclared.length === 1 ? '' : 's'} not declared as network: permissions (${undeclared.slice(0, 5).join(', ')}${undeclared.length > 5 ? ', …' : ''}). Requests to them are blocked by the CSP; links may still be opened via Momentum.open.`,
        count: urls.count, samples: undeclared.slice(0, MAX_SAMPLES),
      });
    }
  }
  push('form-action', 'info', 'Form with an action URL',
    'Form posts only work to declared network: origins (and need the "forms" permission).',
    collect(html, /<form\b[^>]*\baction\s*=/i));
  push('embedded-frames', 'info', 'Embeds frames or plugins',
    '<iframe>/<object>/<embed> are blocked by the CSP (frame-src / object-src none).',
    collect(html, /<(?:iframe|object|embed)\b/i));

  // ── Sensitive browser APIs ────────────────────────────────────────────────
  push('clipboard-read', 'warn', 'Reads the clipboard',
    'navigator.clipboard.read* is denied by the frame\'s permissions policy, but the intent is worth a question.',
    collect(js, /navigator\.clipboard\.read(?:Text)?\s*\(/));
  push('media-or-location', 'warn', 'Camera, microphone or location',
    'getUserMedia / geolocation are denied by the frame\'s permissions policy; the widget expected access it cannot have.',
    collect(js, /getUserMedia\s*\(|navigator\.geolocation|navigator\.mediaDevices/));
  push('keylogging', 'info', 'Global key listeners',
    'Listens to keyboard events on the whole document. Fine for shortcuts; check nothing is sent anywhere.',
    collect(js, /(?:document|window)\.addEventListener\s*\(\s*["']key(?:down|up|press)["']/));

  const order: Record<ScanSeverity, number> = { high: 0, warn: 1, info: 2 };
  return findings.sort((a, b) => order[a.severity] - order[b.severity] || b.count - a.count);
}

export function summarizeScan(findings: ScanFinding[]): ScanSummary {
  const summary: ScanSummary = { high: 0, warn: 0, info: 0, total: findings.length };
  for (const f of findings) summary[f.severity]++;
  return summary;
}
